/**
 * Calendar sagas — async logic, API calls for calendar events.
 */

import { call, put, takeLatest } from 'redux-saga/effects';
import { getCalendarEvents, syncCalendar } from '@/infra/api/client';
import { SYNC_REFRESH } from '@/infra/sync/actions';
import { withSagaFlow, emitSagaPoint } from '@/features/qpl-client/saga-helpers';
import {
  setEvents,
  setCalendarLoading,
  setCalendarError,
  FETCH_EVENTS,
  SYNC_CALENDAR,
} from './ducks';
import { CalendarEvent } from '@/infra/types/domain';

// ─── Fetch Events ──────────────────────────────────────────

export function* fetchEventsSaga(action: {
  type: string;
  payload?: { start?: string; end?: string };
}): Generator {
  yield call(withSagaFlow, 'calendar.fetch_events', ['calendar'], action, function* () {
    yield put(setCalendarLoading(true));
    try {
      const events = (yield call(getCalendarEvents, action.payload)) as CalendarEvent[];
      yield* emitSagaPoint('api_getCalendarEvents_ok', { count: events.length });
      yield put(setEvents(events));
      yield put(setCalendarError(null));
    } catch (error: any) {
      yield put(setCalendarError(error.message || 'Failed to fetch calendar events'));
      yield* emitSagaPoint('api_getCalendarEvents_err', { message: error?.message });
    } finally {
      yield put(setCalendarLoading(false));
    }
  });
}

// ─── Sync Calendar ─────────────────────────────────────────

export function* syncCalendarSaga(action: { type: string }): Generator {
  yield call(withSagaFlow, 'calendar.sync', ['calendar'], action, function* () {
    yield put(setCalendarLoading(true));
    try {
      yield call(syncCalendar);
      yield* emitSagaPoint('api_syncCalendar_ok');
      // After sync, re-fetch events (nested begin/end marker for the refetch)
      yield call(fetchEventsSaga, { type: FETCH_EVENTS });
      yield put(setCalendarError(null));
    } catch (error: any) {
      yield put(setCalendarError(error.message || 'Failed to sync calendar'));
      yield* emitSagaPoint('api_syncCalendar_err', { message: error?.message });
    } finally {
      yield put(setCalendarLoading(false));
    }
  });
}

// ─── WebSocket Event Handlers ──────────────────────────────

function* handleWsCalendarSynced(): Generator {
  // On WS calendar sync event, re-fetch events
  yield call(fetchEventsSaga, { type: FETCH_EVENTS });
}

// ─── Root Calendar Saga ────────────────────────────────────

export function* calendarSaga(): Generator {
  yield takeLatest(FETCH_EVENTS, fetchEventsSaga);
  yield takeLatest(SYNC_CALENDAR, syncCalendarSaga);
  yield takeLatest('WS_CALENDAR_SYNCED', handleWsCalendarSynced);
  yield takeLatest(SYNC_REFRESH, fetchEventsSaga);
}
